/* eslint-disable ember/classic-decorator-hooks */
import EmberObject from '@ember/object';

import ActualFilterAmplitude from './actual-filter-amplitude';
import NoiseGroup from './noise-group';
import IdealOpAmp from './ideal-op-amp';
import HighPassMultipleFeedbackCircuit from './circuit';
import * as CircuitUtils from '../../../utils/circuit';
import * as CommonUtils from '../../../../utils/common';

import * as MultipleFeedbackHighPassCircuitLimitsUtils from './circuit-limits-utils';
import * as ComponentFactory from '../../../components/components-factory';
import { getImplementation } from '../../../gestures/optimization';
import FilterConstants from '../../../../filter-constants';

import { calculateFilterComponents } from '@ebiz/aditools-filter';

// Encapsulates the component creation of multiplefeedback highpass
// A circuit is created from the stage properties stageFp, stageQ, and gain, and the optimization;
export default class MultipleFeedbackHighPassCircuit extends EmberObject {
  // Inputs
  stageFp;
  stageQ;
  gain;
  optimization;
  algorithm;

  // Process output - something used internally but on other classes
  supportedComponents;
  actualFilterAmplitudeModel;
  noiseGroupModel;
  idealOpAmpModel;
  circuitModel;
  idealOpAmpValuePaths;
  componentLimits;
  frequency;
  sizing;

  // Figures output - used internally and also as output in the figures
  needReferenceStage = true;
  c1;
  c2;
  c5;
  r1;
  r2;
  masterCapacitor;
  allowGainChange;
  opAmp;

  // For cGov params of calculateFilterComponents - aditools-filter
  c1IdealValue;

  init() {
    super.init();
    if (
      CommonUtils.isNullOrUndefined(this.stageFp) ||
      CommonUtils.isNullOrUndefined(this.stageQ) ||
      CommonUtils.isNullOrUndefined(this.gain) ||
      CommonUtils.isNullOrUndefined(this.optimization) ||
      CommonUtils.isNullOrUndefined(this.algorithm)
    ) {
      throw 'stageFp, stageQ, gain, optimization, and algorithm should not be null';
    }

    this.actualFilterAmplitudeModel = ActualFilterAmplitude;
    this.noiseGroupModel = NoiseGroup;
    this.idealOpAmpModel = IdealOpAmp;
    this.circuitModel = HighPassMultipleFeedbackCircuit;
    this.supportedComponents = ['r1', 'r2', 'c1', 'c2', 'c5'];
    this.idealOpAmpValuePaths = ['r2', 'c1', 'c5'];

    if (!CommonUtils.isNullOrUndefined(this.initialCapacitorValue)) {
      this.c1IdealValue = this._computeC1IdealValue(this.initialCapacitorValue);
    } else {
      this.c1IdealValue = this._computeC1IdealValue();
    }
    const idealValues = this._computeIdealValues();
    const capacitorLimits = MultipleFeedbackHighPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.c5IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits);
  }

  _computeIdealValues() {
    const implementation = getImplementation(FilterConstants.implementationType.multipleFeedback);
    const c1IdealValue = this.c1IdealValue;
    const params = {
      type: this.algorithm,
      implementation: implementation,
      fp: this.stageFp,
      gain: this.gain,
      q: this.stageQ,
      cGov: c1IdealValue,
    };
    const filterComponents = calculateFilterComponents(
      params.type,
      params.implementation,
      params.fp,
      params.gain,
      params.q,
      params.cGov,
      params.rGainOrCCross,
    );

    return {
      c1IdealValue: filterComponents.c1,
      c2IdealValue: filterComponents.c2,
      c5IdealValue: this._computeC5IdealValue(filterComponents.c1),
      r1IdealValue: filterComponents.r1,
      r2IdealValue: filterComponents.r2,
    };
  }

  _computeC1IdealValue(value) {
    return value ? value : CircuitUtils.computeMasterCapacitorValue(this.optimization, this.stageFp);
  }

  _computeC5IdealValue(value) {
    return value / this.gain;
  }

  _computeComponents(idealValues, capacitorLimits) {
    this.sizing = this._getSizing(idealValues, capacitorLimits);
    this.c1 = ComponentFactory.createC1(
      idealValues.c1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatio,
    );
    this.masterCapacitor = this.c1;
    let rcRatioInverse = CircuitUtils.getInverseOfMasterCapacitorRatio(this.c1);
    this.r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.r2 = ComponentFactory.createR2(
      idealValues.r2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.c2 = ComponentFactory.createC2(
      idealValues.c2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.c5 = ComponentFactory.createC5(
      idealValues.c5IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.frequency = 1 / (2 * Math.PI * Math.sqrt(this.r1.value * this.r2.value * this.c2.value * this.c5.value));
  }

  _getSizing(idealValues, capacitorLimits) {
    let r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
    );
    return CircuitUtils.getResistorSize(r1);
  }

  // Intentionally empty since this is part of the interface.
  // Multiple feedback doesn't support gain resistors
  changeGainValues() {}

  updateR4() {
    throw 'r4 is not supported in this circuit';
  }

  changeInitialCapacitor(value) {
    this.c1IdealValue = this._computeC1IdealValue(value);
    const idealValues = this._computeIdealValues();
    const capacitorLimits = MultipleFeedbackHighPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.c5IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits);
  }
}
